import { Link, useLocation } from "react-router";
import { useMe, useStatusBar } from "@/api/queries";
import { Page } from "@/components/shell/Page";
import { useShell } from "@/components/shell/shell-context";
import { ShortcutRows, SingleKeySwitch } from "@/components/shell/ShortcutsOverlay";
import { GLOBAL_SHORTCUTS, SCREEN_SHORTCUTS, useSingleKeys } from "@/components/shell/shortcuts";
import { Button, ButtonLink } from "@/components/ui/controls";
import { Glyph } from "@/components/ui/Glyph";
import { Lede, Region } from "@/components/ui/Screen";
import styles from "./static.module.css";

interface Row {
  to: string;
  title: string;
  text: string;
}

/** Rows that go somewhere, on hairlines. */
function LinkRows({ rows, label }: { rows: Row[]; label?: string }) {
  return (
    <ul className={styles.rows} aria-label={label}>
      {rows.map((r) => (
        <li key={r.to}>
          <Link to={r.to} className={styles.row}>
            <span className={styles.rowTitle}>{r.title}</span>
            <span className={styles.rowText}>{r.text}</span>
            <Glyph name="chevronRight" size={16} className={styles.rowGo} />
          </Link>
        </li>
      ))}
    </ul>
  );
}

const ENDPOINTS: { method: string; path: string; what: string }[] = [
  { method: "GET", path: "/api/v1/needs-you", what: "What is waiting for a person" },
  { method: "GET", path: "/api/v1/cases", what: "Open and recent cases" },
  { method: "GET", path: "/api/v1/flows", what: "The fixes the crew can run" },
  {
    method: "POST",
    path: "/api/v1/signals/azure-monitor",
    what: "Where Azure Monitor action groups send alerts",
  },
  {
    method: "POST",
    path: "/api/v1/signals/grafana",
    what: "Where Grafana contact points send alerts",
  },
  { method: "GET", path: "/api/v1/audit", what: "The audit log, filtered and a page at a time" },
  { method: "GET", path: "/api/v1/audit/export.csv", what: "The audit log as CSV, for admins" },
  { method: "POST", path: "/api/v1/audit/verify", what: "Re-check every hash in the audit chain" },
  { method: "GET", path: "/api/v1/usage", what: "This month's model calls, tokens and cost" },
  {
    method: "GET",
    path: "/api/v1/knowledge/search",
    what: "Runbooks and articles that answer a question, quoted",
  },
];

export function HelpPage() {
  const status = useStatusBar();
  const me = useMe();
  const shell = useShell();
  const singleKeys = useSingleKeys();
  const where = me.data
    ? `, connected to ${me.data.tenant.name}, ${me.data.tenant.environment_label}`
    : "";
  const title = status.data
    ? `You're on ZeaOps ${status.data.version}${where}.`
    : "Help for getting around ZeaOps.";
  return (
    <Page heading="Help" primary={null} dots={false} mainStyle={{ gap: 26 }}>
      <Lede
        title={title}
        line="Press Ctrl K anywhere to find a case, a flow or a template, connect a system, or ask the crew. The API every screen reads from is described below."
      />
      <Region
        first
        title="Keyboard shortcuts"
        aside={
          <Button size="sm" onClick={() => shell.openShortcuts()}>
            Show them over any screen
          </Button>
        }
      >
        <p className={styles.prose}>
          Press <kbd className={styles.kbd}>?</kbd> on any screen to see these, with the ones for
          that screen first.
        </p>
        <div className={styles.shortcutGroups}>
          {[...GLOBAL_SHORTCUTS, ...SCREEN_SHORTCUTS].map((g) => (
            <section key={g.title} className={styles.shortcutGroup} aria-label={g.title}>
              <h4 className={styles.groupTitle}>{g.title}</h4>
              <ShortcutRows group={g} singleKeys={singleKeys} />
            </section>
          ))}
        </div>
        <SingleKeySwitch />
      </Region>
      <Region
        title="API reference"
        aside={
          <a href="/api/docs" target="_blank" rel="noreferrer" className={styles.apiLink}>
            Open the API reference
            <Glyph name="external" size={13} />
          </a>
        }
      >
        <p className={styles.prose}>
          Every screen reads from the same documented API, which you can call with your own token. A
          few places to start:
        </p>
        <div className={styles.endpoints} role="table" aria-label="Some API endpoints">
          <div role="rowgroup">
            <div role="row" className={`${styles.endpoint} ${styles.endpointHead}`}>
              <span role="columnheader">Method</span>
              <span role="columnheader">Path</span>
              <span role="columnheader">What it gives you</span>
            </div>
          </div>
          <div role="rowgroup">
            {ENDPOINTS.map((e) => (
              <div key={e.path} role="row" className={styles.endpoint}>
                <span role="cell" className={styles.method}>
                  {e.method}
                </span>
                <span role="cell" className={styles.path}>
                  {e.path}
                </span>
                <span role="cell" className={styles.endpointText}>
                  {e.what}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Region>
    </Page>
  );
}

export function NotFoundPage() {
  const { pathname } = useLocation();
  return (
    <Page heading="Not found" primary={null} dots={false} mainStyle={{ gap: 26 }}>
      <Lede
        title="There's nothing at this address."
        line={
          <>
            Nothing lives at <code className={styles.path}>{pathname}</code>. The link may be old,
            or the item was removed.
          </>
        }
        actions={
          <ButtonLink to="/" variant="primary">
            Go to Home
          </ButtonLink>
        }
      />
      <Region first title="Or pick up from here">
        <LinkRows
          rows={[
            {
              to: "/needs-you",
              title: "Needs you",
              text: "Fixes and handoffs waiting for a person.",
            },
            {
              to: "/cases",
              title: "Cases",
              text: "Everything the crew is working on or has closed.",
            },
            { to: "/flows", title: "Flows", text: "The fixes the crew knows how to run." },
            { to: "/ask", title: "Ask ZeaOps", text: "Ask about anything the crew has seen." },
          ]}
        />
      </Region>
    </Page>
  );
}
